import { useExpenses } from "@/contexts/expense-context";
import { ExpenseCard } from "@/components/expense-card";
import { BottomNav } from "@/components/bottom-nav";
import { UpcomingRecurringWidget } from "@/components/upcoming-recurring";
import { formatCurrency, convertCurrency } from "@/lib/utils/currency";
import { useMemo, lazy, Suspense } from "react";
import { ChartSkeleton, Skeleton } from "@/components/skeleton";

// Lazy load chart component (reduces initial bundle size)
const YearlyChart = lazy(() => import("@/components/yearly-chart").then(m => ({ default: m.YearlyChart })));

export default function DashboardPage() {
    const { expenses, currentUser, loading, error, defaultCurrency } = useExpenses();

    const monthlyTotal = useMemo(() => {
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        return expenses
            .filter((expense) => {
                const expenseDate = new Date(expense.date);
                return (
                    expenseDate.getMonth() === currentMonth &&
                    expenseDate.getFullYear() === currentYear
                );
            })
            .reduce((sum, expense) => {
                // Convert each expense to user's default currency before summing
                const amountInDefaultCurrency = convertCurrency(
                    expense.amount,
                    expense.currency,
                    defaultCurrency
                );
                return sum + amountInDefaultCurrency;
            }, 0);
    }, [expenses, defaultCurrency]);

    const recentExpenses = useMemo(() => {
        return [...expenses]
            .sort(
                (a, b) =>
                    new Date(b.date).getTime() - new Date(a.date).getTime()
            )
            .slice(0, 5);
    }, [expenses]);

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-4xl mx-auto px-4 py-6">
                    <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
                    {currentUser && (
                        <p className="text-gray-600 mt-1">Welcome back, {currentUser.name}</p>
                    )}
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="max-w-4xl mx-auto px-4 pt-4">
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                        {error}
                    </div>
                </div>
            )}

            {/* Main Content */}
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Monthly Overview */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                    <h2 className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                        This Month
                    </h2>
                    {loading ? (
                        <Skeleton className="h-12 w-48" />
                    ) : (
                        <p className="text-4xl font-bold text-teal-600">
                            {formatCurrency(monthlyTotal, defaultCurrency)}
                        </p>
                    )}
                </div>

                {/* Upcoming Recurring Expenses */}
                <div className="mb-8">
                    <UpcomingRecurringWidget />
                </div>

                {/* Yearly Chart */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        Yearly Overview
                    </h2>
                    <Suspense fallback={<ChartSkeleton />}>
                        <YearlyChart />
                    </Suspense>
                </div>

                {/* Recent Expenses */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        Recent Expenses
                    </h2>
                    <div className="space-y-3">
                        {loading ? (
                            <p className="text-gray-500 text-center py-8">
                                Loading expenses...
                            </p>
                        ) : recentExpenses.length > 0 ? (
                            recentExpenses.map((expense) => (
                                <ExpenseCard key={expense.id} expense={expense} />
                            ))
                        ) : (
                            <p className="text-gray-500 text-center py-8">
                                No expenses yet. Start tracking!
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Bottom Navigation */}
            <BottomNav />
        </div>
    );
}
